import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Shield, Copy, Check, AlertTriangle, Key } from "lucide-react";
import * as OTPAuth from "otpauth";
import QRCode from "qrcode";
import { useLanguage } from "@/contexts/LanguageContext";

interface MFASetupProps {
  userId: string;
  userEmail: string;
  onComplete?: () => void;
}

export const MFASetup = ({ userId, userEmail, onComplete }: MFASetupProps) => {
  const { t } = useLanguage();
  const [step, setStep] = useState<'check' | 'setup' | 'verify' | 'complete'>('check');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [secret, setSecret] = useState<string>('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copiedBackupCodes, setCopiedBackupCodes] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    checkMFAStatus();
  }, [userId]);

  const checkMFAStatus = async () => {
    const { data, error } = await supabase
      .from('user_mfa_settings')
      .select('mfa_enabled')
      .eq('user_id', userId)
      .maybeSingle();

    if (!error && data) {
      setMfaEnabled(data.mfa_enabled);
      setStep(data.mfa_enabled ? 'complete' : 'setup');
    } else {
      setStep('setup');
    }
  };

  const generateMFASecret = async () => {
    setLoading(true);
    try {
      // Generate TOTP secret
      const totp = new OTPAuth.TOTP({
        issuer: 'Church App',
        label: userEmail,
        algorithm: 'SHA1',
        digits: 6,
        period: 30,
      });

      const newSecret = totp.secret.base32;
      setSecret(newSecret);

      // Generate QR code
      const otpauthUrl = totp.toString();
      const qrUrl = await QRCode.toDataURL(otpauthUrl);
      setQrCodeUrl(qrUrl);

      // Generate backup codes
      const { data: codes } = await supabase.rpc('generate_backup_codes', { count: 10 });
      if (codes) {
        setBackupCodes(codes);
      }

      setStep('verify');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to generate MFA secret",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast({
        title: "Invalid code",
        description: "Please enter a 6-digit code",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Verify the TOTP code
      const totp = new OTPAuth.TOTP({
        issuer: 'Church App',
        label: userEmail,
        algorithm: 'SHA1',
        digits: 6,
        period: 30,
        secret: OTPAuth.Secret.fromBase32(secret),
      });

      const delta = totp.validate({ token: verificationCode, window: 1 });
      
      if (delta === null) {
        toast({
          title: "Invalid code",
          description: "The code you entered is incorrect. Please try again.",
          variant: "destructive",
        });
        return;
      }

      // Check if MFA settings record already exists
      const { data: existingSettings } = await supabase
        .from('user_mfa_settings')
        .select('user_id')
        .eq('user_id', userId)
        .maybeSingle();

      let error;
      
      if (existingSettings) {
        // Update existing record
        const result = await supabase
          .from('user_mfa_settings')
          .update({
            mfa_enabled: true,
            mfa_secret: secret,
            backup_codes: backupCodes,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', userId);
        error = result.error;
      } else {
        // Insert new record
        const result = await supabase
          .from('user_mfa_settings')
          .insert({
            user_id: userId,
            mfa_enabled: true,
            mfa_secret: secret,
            backup_codes: backupCodes,
          });
        error = result.error;
      }

      if (error) throw error;

      // Log successful MFA setup
      await supabase.rpc('log_auth_event', {
        _user_id: userId,
        _event_type: 'mfa_enabled',
        _metadata: { method: 'totp' }
      });

      setMfaEnabled(true);
      setStep('complete');
      
      toast({
        title: "MFA Enabled",
        description: "Two-factor authentication has been successfully enabled",
      });

      onComplete?.();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to enable MFA",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const disableMFA = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('user_mfa_settings')
        .update({ mfa_enabled: false, mfa_secret: null, backup_codes: null })
        .eq('user_id', userId);

      if (error) throw error;

      // Log MFA disabled
      await supabase.rpc('log_auth_event', {
        _user_id: userId,
        _event_type: 'mfa_disabled'
      });

      setMfaEnabled(false);
      setStep('setup');
      
      toast({
        title: "MFA Disabled",
        description: "Two-factor authentication has been disabled",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to disable MFA",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setCopiedBackupCodes(true);
    toast({
      title: "Copied",
      description: "Backup codes copied to clipboard",
    });
    setTimeout(() => setCopiedBackupCodes(false), 2000);
  };

  if (step === 'complete' && mfaEnabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-green-600" />
            {t("mfa.title")}
          </CardTitle>
          <CardDescription>
            {t("mfa.protectedDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Check className="h-4 w-4" />
            <AlertDescription>
              {t("mfa.enabledMessage")}
            </AlertDescription>
          </Alert>
          
          <Button
            variant="destructive"
            onClick={disableMFA}
            disabled={loading}
          >
            {t("mfa.disable")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (step === 'setup') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {t("mfa.enable")}
          </CardTitle>
          <CardDescription>
            {t("mfa.setupDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("mfa.setupExplanation")}
          </p>
          
          <Alert>
            <Key className="h-4 w-4" />
            <AlertDescription>
              {t("mfa.authenticatorRequired")}
            </AlertDescription>
          </Alert>

          <Button
            onClick={generateMFASecret}
            disabled={loading}
            className="w-full"
          >
            {t("mfa.setupButton")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (step === 'verify') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Scan QR Code</CardTitle>
          <CardDescription>
            Use your authenticator app to scan this QR code
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col items-center space-y-4">
            {qrCodeUrl && (
              <img src={qrCodeUrl} alt="QR Code" className="w-64 h-64 border-2 border-border rounded-lg" />
            )}
            
            <Alert className="border-border">
              <AlertDescription className="text-xs">
                <p className="font-medium mb-1">Can't scan the QR code?</p>
                <p>Enter this code manually: <code className="bg-muted/20 text-foreground px-2 py-1 rounded font-mono border border-border">{secret}</code></p>
              </AlertDescription>
            </Alert>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="verification-code" className="text-foreground">Enter 6-digit code from your app</Label>
              <Input
                id="verification-code"
                type="text"
                maxLength={6}
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest bg-background text-foreground border-input"
              />
            </div>

            <Button
              onClick={verifyAndEnable}
              disabled={loading || verificationCode.length !== 6}
              className="w-full"
            >
              Verify and Enable
            </Button>
          </div>

          {backupCodes.length > 0 && (
            <Alert variant="default" className="border-border">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <p className="font-medium mb-2">Save Your Backup Codes</p>
                <p className="text-xs mb-3">
                  Store these codes in a safe place. You can use them to access your account if you lose your phone.
                </p>
                <div className="bg-muted/20 p-3 rounded-md mb-2 max-h-32 overflow-y-auto border border-border">
                  <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                    {backupCodes.map((code, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Badge variant="outline">{index + 1}</Badge>
                        <span>{code}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyBackupCodes}
                  className="w-full"
                >
                  {copiedBackupCodes ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                  {copiedBackupCodes ? 'Copied!' : 'Copy All Codes'}
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  }

  return null;
};
